import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getSparksFiltered, createSpark, reportSpark, likeSpark, subscribeToDailyChallenge, completeDailyChallenge, getFriends, getMagicSparkHolder, checkAndUnlockBadges, isSparkLiked, deleteSpark, subscribeToSparks, subscribeToBannedUsers, regenerateDailyChallenge, restoreDailyChallenge, subscribeToDailyChallengeHistory, reactToSpark, checkDailySparkLimit, uploadSparkImage, getSpark } from '../services/firestore.js';
import { getMuseHistory } from '../services/ai.js';
import { auth } from '../services/firebase.js';
import { Image as ImageIcon, Smile, Send, MoreVertical, Heart, MessageCircle, Share2, Sparkles, X, Flag, AlertTriangle, Play, Music, Box, Trophy, Flame, Trash2, Shield, Clock, RotateCcw, Youtube, Twitch, Video, Headphones, Link, Info, Download, Copy, Camera } from 'lucide-react';
import ReactPlayer from 'react-player';
import EmotePicker from '../components/EmotePicker.jsx';
import CameraModal from '../components/CameraModal.jsx'; // Import CameraModal
import EmojiReactionBar from '../components/EmojiReactionBar.jsx';
import LiveUserBadge from '../components/LiveUserBadge.jsx';
import { useEmoteTheme } from '../hooks/useEmoteTheme.js';
import { useAppearance } from '../hooks/useAppearance.js';
import html2canvas from 'html2canvas';
import { SparkShareCard } from '../components/SparkShareCard.jsx';

export default function Feed({ onViewProfile, user, initialSparkId }) {
    const { getEmoteObject, checkEmoteAccess, getEmoteUrl } = useEmoteTheme();
    const { appearance } = useAppearance();
    const { fontSize, emoteSize } = appearance.feed;
    const [newSpark, setNewSpark] = useState("");
    const [sparks, setSparks] = useState([]);

    // ... (rest of state) ...
    // IMAGE HANDLING STATS
    const [attachments, setAttachments] = useState([]); // Array of { id, type, file, preview }
    const [urlInput, setUrlInput] = useState("");
    const [showImageInput, setShowImageInput] = useState(false);

    const TIER_IMAGE_LIMITS = {
        'global': 1,
        'subscriber': 2,
        'super_subscriber': 4,
        'superSubscriber': 4,
        'mega_fan': 6
    };

    const getImageLimit = () => {
        if (user?.isSuperAdmin) return 99;
        if (user?.isAdmin) return 6;
        return TIER_IMAGE_LIMITS[user?.subscriptionTier] || 1;
    };
    const [magicSparkHolder, setMagicSparkHolder] = useState(null);
    const [history, setHistory] = useState([]);
    const [selectedInspiration, setSelectedInspiration] = useState(null);
    const [selectedInspirationType, setSelectedInspirationType] = useState('muse'); // 'muse' or 'challenge'
    const [dailyChallenge, setDailyChallenge] = useState(null);
    const [friends, setFriends] = useState([]);
    const [likedSparks, setLikedSparks] = useState({});
    const [bannedUserIds, setBannedUserIds] = useState([]);
    const [challengeHistory, setChallengeHistory] = useState([]);
    const [showAdminHistoryDropdown, setShowAdminHistoryDropdown] = useState(false);
    const [showInspirationDropdown, setShowInspirationDropdown] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(null);
    const [sparkReactions, setSparkReactions] = useState({});
    const [showLimitModal, setShowLimitModal] = useState(false);
    const [limitDetails, setLimitDetails] = useState({ limit: 0, count: 0 });

    const latestSparkIdRef = React.useRef(null);
    const initialLoadRef = React.useRef(true);

    useEffect(() => {
        if (!user?.uid) return;

        let unsubscribeSparks = null;
        let unsubscribeBanned = null;

        const loadData = async () => {
            try {
                // Static data fetch (History, Friends, Magic Holder)
                // Note: We separated Sparks to be real-time below
                // Independent loading to prevent cascade failure
                const [historyResult, friendsResult, magicResult] = await Promise.allSettled([
                    getMuseHistory(user.uid),
                    getFriends(user.uid),
                    getMagicSparkHolder()
                ]);

                // Log errors for debugging
                if (historyResult.status === 'rejected') console.error('Muse History failed:', historyResult.reason);
                if (friendsResult.status === 'rejected') console.error('Friends failed:', friendsResult.reason);
                if (magicResult.status === 'rejected') console.error('Magic Spark failed:', magicResult.reason);

                // Set state with successful data or defaults
                setHistory(historyResult.status === 'fulfilled' ? historyResult.value : []);
                setFriends(friendsResult.status === 'fulfilled' ? friendsResult.value : []);
                setMagicSparkHolder(magicResult.status === 'fulfilled' ? magicResult.value?.holderName : null);


                // Real-time Sparks Subscription
                unsubscribeSparks = subscribeToSparks(user.uid, (updatedSparks) => {
                    setSparks(updatedSparks);

                    // Update liked status based on new data
                    const liked = {};
                    const reactions = {};
                    for (const spark of updatedSparks) {
                        if (spark.likedBy && spark.likedBy.includes(user.uid)) {
                            liked[spark.id] = true;
                        }
                        // Sync reactions from Firestore to local state
                        if (spark.reactions) {
                            reactions[spark.id] = spark.reactions;
                        }
                    }
                    setLikedSparks(liked);
                    setSparkReactions(reactions);
                });

                // Real-time Banned Users Subscription
                unsubscribeBanned = subscribeToBannedUsers((bannedIds) => {
                    setBannedUserIds(bannedIds);
                });

                // Real-time Daily Challenge Subscription
                // This ensures that when the date rolls over or a challenge is generated, everyone sees it instantly.
                const unsubscribeChallenge = subscribeToDailyChallenge((challenge) => {
                    setDailyChallenge(challenge);
                });

                // Real-time History Subscription
                let unsubscribeHistory = null;
                if (showAdminHistoryDropdown && user?.isSuperAdmin) {
                    unsubscribeHistory = subscribeToDailyChallengeHistory((history) => {
                        setChallengeHistory(history);
                    });
                }

                // Add to cleanup
                return () => {
                    if (unsubscribeChallenge) unsubscribeChallenge();
                    if (unsubscribeHistory) unsubscribeHistory();
                };

            } catch (error) {
                console.error('Error loading feed data:', error);
            }
        };



        const cleanupPromise = loadData();

        return () => {
            if (unsubscribeSparks) unsubscribeSparks();
            if (unsubscribeBanned) unsubscribeBanned();
            cleanupPromise.then(cleanup => {
                if (cleanup) cleanup();
            });
        };
    }, [user, showAdminHistoryDropdown]);

    // Live Spark Effect
    useEffect(() => {
        if (sparks.length > 0) {
            const newestSpark = sparks[0];

            if (initialLoadRef.current) {
                latestSparkIdRef.current = newestSpark.id;
                initialLoadRef.current = false;
                return;
            }

            if (newestSpark.id !== latestSparkIdRef.current) {
                latestSparkIdRef.current = newestSpark.id;

                if (user?.uid && newestSpark.authorId !== user.uid) {
                    console.log("Live Spark Received! Triggering Effect for:", newestSpark.id);
                    setTimeout(() => {
                        triggerSparkleBurst(newestSpark.id, newestSpark.inspirationType);
                    }, 500);
                }
            }
        }
    }, [sparks, user]);

    // DEEP LINKING LOGIC
    // We can't rely solely on the subscription because the linked spark might be old (not in the first 50).
    // So we fetch it explicitly if we have an ID.
    useEffect(() => {
        // Only run if we have an ID
        if (initialSparkId) {
            const highlightSpark = async () => {
                // 1. Check if it's already in the list
                const alreadyLoaded = sparks.some(s => s.id === initialSparkId);

                if (!alreadyLoaded) {
                    // 2. Fetch it specifically
                    const fetchedSpark = await getSpark(initialSparkId);
                    if (fetchedSpark) {
                        console.log("Deep Link: Loaded missing spark", fetchedSpark.id);
                        // Prepend to top temporarily (the subscription will eventually overwrite if it updates, 
                        // but usually real-time only appends NEW stuff or updates existing. 
                        // If we prepend an OLD spark, the subscription *might* not remove it depending on logic, 
                        // but to be safe we just add it to local state).
                        setSparks(prev => {
                            // Double check inside setter to avoid race conditions
                            if (prev.some(s => s.id === initialSparkId)) return prev;
                            return [fetchedSpark, ...prev];
                        });
                    }
                }

                // 3. Scroll to it (Delay slightly to allow render)
                setTimeout(() => {
                    const el = document.getElementById(`spark-${initialSparkId}`);
                    if (el) {
                        // Ensure it clears the sticky header
                        el.style.scrollMarginTop = '100px'; // Approx header height + padding
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        // Optional: Add a visual flash effect via class
                        el.classList.add('highlight-flash');
                        setTimeout(() => el.classList.remove('highlight-flash'), 2000);
                    } else {
                        console.log("Deep Link: Element not found in DOM yet.");
                    }
                }, 1000);
            };

            highlightSpark();
        }
    }, [initialSparkId, sparks.length]); // Dependencies need care to not loop

    // ... existing helpers ...
    const formatTimeAgo = (timestamp) => {
        if (!timestamp) return 'Just now';

        const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);

        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;

        return date.toLocaleDateString();
    };

    const isFriend = (userId) => {
        return friends.some(friend => friend.id === userId);
    };

    const [showPostEmotePicker, setShowPostEmotePicker] = useState(false);
    const [showLinkConfirmModal, setShowLinkConfirmModal] = useState(false);

    const triggerSparkleBurst = (targetId = null, inspirationType = 'muse') => {
        // Attempt to find target if provided
        let rect = null;
        if (targetId) {
            const el = document.getElementById(`spark-${targetId}`);
            if (el) {
                rect = el.getBoundingClientRect();

                // Determine Glow Class based on Inspiration Type
                const glowClass = inspirationType === 'challenge' ? 'new-spark-glow-challenge' : 'new-spark-glow-muse';

                // Apply Glow Class
                el.classList.add(glowClass);
                setTimeout(() => el.classList.remove(glowClass), 3000);
            }
        }

        const colors = ['#ec4899', '#8b5cf6', '#f59e0b', '#10b981', '#3b82f6', '#ffffff'];
        const particleCount = targetId && rect ? 80 : 150;

        for (let i = 0; i < particleCount; i++) {
            const el = document.createElement('div');

            // Positioning Logic
            let startX, startY;
            let angleToUse = Math.random() * Math.PI * 2;

            if (rect) {
                // Spawn along the edges (Framing Effect)
                const perimeter = (rect.width + rect.height) * 2;
                const pos = Math.random() * perimeter;

                if (pos < rect.width) { // Top
                    startX = rect.left + pos;
                    startY = rect.top;
                } else if (pos < rect.width + rect.height) { // Right
                    startX = rect.right;
                    startY = rect.top + (pos - rect.width);
                } else if (pos < rect.width * 2 + rect.height) { // Bottom
                    startX = rect.right - (pos - (rect.width + rect.height));
                    startY = rect.bottom;
                } else { // Left
                    startX = rect.left;
                    startY = rect.bottom - (pos - (rect.width * 2 + rect.height));
                }

                // Burst OUTWARD from center of card
                const centerX = rect.left + rect.width / 2;
                const centerY = rect.top + rect.height / 2;
                const angleFromCenter = Math.atan2(startY - centerY, startX - centerX);
                angleToUse = angleFromCenter + (Math.random() - 0.5) * 0.5;

            } else {
                // Center Screen Fallback
                startX = window.innerWidth / 2;
                startY = window.innerHeight / 2;
            }

            el.style.cssText = `position:fixed;left:${startX}px;top:${startY}px;width:${Math.random() * 20 + 8}px;height:${Math.random() * 20 + 8}px;background:${colors[Math.floor(Math.random() * colors.length)]};border-radius:50%;pointer-events:none;z-index:99999;box-shadow:0 0 15px ${colors[Math.floor(Math.random() * colors.length)]}`;

            const velocity = Math.random() * 400 + 100; // Fast expansion
            const tx = Math.cos(angleToUse) * velocity;
            const ty = Math.sin(angleToUse) * velocity;

            el.animate([
                { transform: 'translate(-50%, -50%) scale(0.5)', opacity: 1 },
                { transform: `translate(calc(-50% + ${tx}px), calc(-50% + ${ty}px)) scale(0)`, opacity: 0 }
            ], { duration: 1200 + Math.random() * 600, easing: 'cubic-bezier(0, .9, .57, 1)' }).onfinish = () => el.remove();
            document.body.appendChild(el);
        }
    };

    const scrollToToolbar = () => {
        setTimeout(() => {
            document.getElementById('spark-controls-toolbar')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
    };

    const handlePost = async () => {
        // If nothing linked, ask user
        if (!selectedInspiration) {
            setShowLinkConfirmModal(true);
            return;
        }

        // Check Token Usage Limit
        const tokenMatches = newSpark.match(/(\(image:\d+\)|\(media:\d+\))/g) || [];
        if (tokenMatches.length > getImageLimit()) {
            alert(`Too many media items placed! Your limit is ${getImageLimit()}. You have placed ${tokenMatches.length}.`);
            return;
        }

        if (!newSpark.trim() && attachments.length === 0) return;
        if (!user?.uid) return;

        // Check IMAGE Limit (Frontend Check just in case)
        if (attachments.length > getImageLimit()) {
            alert(`Too many media items! Limit is ${getImageLimit()}.`);
            return;
        }

        // Check Limit
        try {
            console.log("DEBUG: Starting limit check for user:", user.uid);
            const limitCheck = await checkDailySparkLimit(user.uid, user.subscriptionTier, user.isAdmin, user.isSuperAdmin);
            console.log("DEBUG: Limit check result:", limitCheck);

            if (!limitCheck.allowed) {
                console.log("DEBUG: Limit reached! Showing modal.");
                setLimitDetails({ limit: limitCheck.limit, count: limitCheck.count });
                setShowLimitModal(true);
                return;
            }
            console.log("DEBUG: Limit allowed. Proceeding.");
        } catch (e) {
            console.error("DEBUG: Error in limit check:", e);
        }

        try {
            // Upload Images
            const uploadedImageUrls = [];
            for (const att of attachments) {
                if (att.type === 'file') {
                    const url = await uploadSparkImage(user.uid, att.file);
                    uploadedImageUrls.push(url);
                } else if (att.type === 'url') {
                    uploadedImageUrls.push(att.preview);
                }
            }

            // Ensure Tokens exist in Text (Auto-Append if missing)
            let finalText = newSpark;
            if (uploadedImageUrls.length > 0) {
                uploadedImageUrls.forEach((url, i) => {
                    const token = `(media:${i + 1})`;
                    if (!finalText.includes(token)) {
                        // Decide whether to append with newline or just append
                        finalText = finalText.trim() + (finalText.trim() ? `\n\n` : ``) + token;
                    }
                });
            }

            const sparkData = {
                author: user.name,
                authorId: user.id || user.uid,
                content: finalText,
                inspiration: selectedInspiration, // Strict: No auto-fallback
                inspirationType: selectedInspirationType,
                authorAvatar: user.avatar,
                authorTier: user.isSuperAdmin ? 'super_admin' : (user.isAdmin ? 'admin' : (user.subscriptionTier || 'global')),
                images: uploadedImageUrls,
                imageUrl: uploadedImageUrls[0] || null // Legacy fallback
            };

            const newSparkDoc = await createSpark(sparkData);

            setTimeout(() => {
                const el = document.getElementById(`spark-${newSparkDoc.id}`);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    triggerSparkleBurst(newSparkDoc.id, selectedInspirationType);
                } else {
                    triggerSparkleBurst();
                }
            }, 600);

            setNewSpark("");
            resetImageState();
            setSelectedInspiration(null);
            setSelectedInspirationType('muse');
        } catch (error) {
            console.error('Error posting spark:', error);
            alert('Failed to post spark. Please try again.');
        }
    };


    const handleReport = async (sparkId) => {
        if (!user?.uid) return;
        if (window.confirm("Report this content as inappropriate? It will be hidden for you and sent to admins.")) {
            try {
                // Optimistically remove from feed
                setSparks(prev => prev.filter(s => s.id !== sparkId));

                await reportSpark(sparkId, user.uid);
                alert("Thanks for keeping our community safe.");
            } catch (error) {
                console.error('Error reporting spark:', error);
                alert('Failed to report. Please try again.');
            }
        }
    };

    const handleDelete = async (sparkId) => {
        if (!user?.uid) return;
        if (window.confirm("Are you sure you want to delete this spark? This action cannot be undone and will remove 1 Spark from your stats.")) {
            try {
                await deleteSpark(sparkId, user.uid);
                // Optimistically remove from feed
                setSparks(prev => prev.filter(s => s.id !== sparkId));
            } catch (error) {
                console.error("Error deleting spark:", error);
                alert("Failed to delete spark.");
            }
        }
    };

    const handleLike = async (sparkId) => {
        if (!user?.uid) return;

        // Optimistic update
        const isLiked = !!likedSparks[sparkId];
        setLikedSparks(prev => ({ ...prev, [sparkId]: !isLiked }));
        setSparks(prev => prev.map(s => {
            if (s.id === sparkId) {
                return { ...s, likes: (s.likes || 0) + (isLiked ? -1 : 1) };
            }
            return s;
        }));

        try {
            const result = await likeSpark(sparkId, user.uid);
            if (result) {
                // Confirm state with server result
                setLikedSparks(prev => ({ ...prev, [sparkId]: result.liked }));
                setSparks(prev => prev.map(s => {
                    if (s.id === sparkId) {
                        return { ...s, likes: result.newCount };
                    }
                    return s;
                }));
            }
        } catch (error) {
            console.error('Error liking spark:', error);
            // Revert on error
            setLikedSparks(prev => ({ ...prev, [sparkId]: isLiked }));
            setSparks(prev => prev.map(s => {
                if (s.id === sparkId) {
                    return { ...s, likes: (s.likes || 0) + (isLiked ? 1 : -1) };
                }
                return s;
            }));
        }
    };

    // Share Logic (Moved out of handleLike)
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [activeShareSpark, setActiveShareSpark] = useState(null);
    const [generatedCardImage, setGeneratedCardImage] = useState(null);
    const [isGeneratingCard, setIsGeneratingCard] = useState(false);
    const cardRef = React.useRef(null);

    const handleShareClick = (spark) => {
        setActiveShareSpark(spark);
        setShareModalOpen(true);
        setGeneratedCardImage(null); // Reset previous
    };

    const copyShareLink = async () => {
        if (!activeShareSpark) return;
        const text = `Check out this Spark by ${activeShareSpark.author}: "${activeShareSpark.content || 'Art'}"`;
        const shareUrl = `${window.location.origin}/?spark=${activeShareSpark.id}`;

        if (navigator.share) {
            try {
                await navigator.share({ title: 'Spark Inspiration', text: text, url: shareUrl });
            } catch (err) { console.error(err); }
        } else {
            navigator.clipboard.writeText(`${text}\n${shareUrl}`);
            alert("Link copied to clipboard!");
        }
        setShareModalOpen(false);
    };

    const generateShareCard = async () => {
        if (!cardRef.current) return;
        setIsGeneratingCard(true);
        try {
            // 1. Force a small delay to ensure DOM render
            await new Promise(resolve => setTimeout(resolve, 100));

            // 2. Explicitly wait for all images in the card to load
            const images = Array.from(cardRef.current.querySelectorAll('img'));
            const imagePromises = images.map(img => {
                if (img.complete) return Promise.resolve();
                return new Promise(resolve => {
                    img.onload = resolve;
                    img.onerror = resolve; // Continue even if error
                    // Fallback timeout for individual images
                    setTimeout(resolve, 3000);
                });
            });

            // Wait for all images or max 3.5 seconds
            await Promise.race([
                Promise.all(imagePromises),
                new Promise(resolve => setTimeout(resolve, 3500))
            ]);

            const canvas = await html2canvas(cardRef.current, {
                backgroundColor: null,
                scale: 2, // Retina quality
                useCORS: true, // Critical for avatars/images
                logging: true, // Help debug
                allowTaint: false // We need dataURL, so allowTaint: true is forbidden
            });

            setGeneratedCardImage(canvas.toDataURL('image/png'));
        } catch (error) {
            console.error("Card generation failed:", error);
            alert("Could not generate card. Please try again.");
        } finally {
            setIsGeneratingCard(false);
        }
    };


    const [showCameraModal, setShowCameraModal] = useState(false);

    const processImageFile = (file) => {
        if (!file) return;

        const limit = getImageLimit();
        if (attachments.length >= limit) {
            alert(`Limit reached! Your tier (${user?.subscriptionTier || 'global'}) can only attach ${limit} media item(s).`);
            return;
        }

        // Compress image logic
        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;
                const maxSize = 800; // Slightly larger for feed images
                if (width > height) {
                    if (width > maxSize) {
                        height *= maxSize / width;
                        width = maxSize;
                    }
                } else {
                    if (height > maxSize) {
                        width *= maxSize / height;
                        height = maxSize;
                    }
                }
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                const compressedBase64 = canvas.toDataURL('image/jpeg', 0.8); // 80% quality for feed

                const newItem = {
                    id: Date.now().toString(),
                    type: 'file',
                    file: file,
                    preview: compressedBase64
                };

                setAttachments(prev => [...prev, newItem]);

                // Insert token
                const token = ` (media:${attachments.length + 1}) `;
                setNewSpark(prev => prev + token);
                setShowImageInput(false); // Close input panel
                setShowCameraModal(false); // Close camera if open
                scrollToToolbar();
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(file);
    };

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        processImageFile(file);
    };

    const handleCameraCapture = (file) => {
        processImageFile(file);
    };

    const handleAddUrlImage = () => {
        if (!urlInput.trim()) return;

        const limit = getImageLimit();
        if (attachments.length >= limit) {
            alert(`Limit reached! Your tier (${user?.subscriptionTier || 'global'}) can only attach ${limit} media item(s).`);
            return;
        }

        const newItem = {
            id: Date.now().toString(),
            type: 'url',
            file: null,
            preview: urlInput
        };

        setAttachments(prev => [...prev, newItem]);
        // Insert token
        const token = ` (media:${attachments.length + 1}) `;
        setNewSpark(prev => prev + token);

        setUrlInput("");
        setShowImageInput(false);
        scrollToToolbar();
    };

    const removeAttachment = (id, index) => {
        setAttachments(prev => prev.filter(item => item.id !== id));

        // Text Cleanup Logic
        setNewSpark(currentText => {
            // 1. Remove the specific token: (media:index+1)
            const targetToken = `(media:${index + 1})`;
            let text = currentText.split(targetToken).join('');

            // 2. Shift subsequent tokens down: (media:4) -> (media:3)
            // We iterate from index+2 up to prev.length (original count)
            // It's safer to find ALL (media:N) tokens and adjust them if N > index+1
            text = text.replace(/(\(image:(\d+)\)|\(media:(\d+)\))/g, (match, fullToken, imageNum, mediaNum) => {
                const num = parseInt(imageNum || mediaNum, 10);
                if (num > index + 1) {
                    // Preserve original token type (image or media)
                    if (imageNum) return `(image:${num - 1})`;
                    if (mediaNum) return `(media:${num - 1})`;
                }
                return match;
            });

            return text;
        });
        scrollToToolbar();
    };

    const resetImageState = () => {
        setAttachments([]);
        setUrlInput("");
        setShowImageInput(false);
    };

    const handleLoadHistory = () => {
        setShowAdminHistoryDropdown(!showAdminHistoryDropdown);
    };

    const handleRestoreChallenge = async (pastChallenge) => {
        if (window.confirm(`Restore this challenge: "${pastChallenge.prompt}"?`)) {
            await restoreDailyChallenge(pastChallenge);
            // Close dropdown
            setShowAdminHistoryDropdown(false);
        }
    };

    const handleOpenEmojiPicker = (sparkId) => {
        setShowEmojiPicker(sparkId);
    };

    const handleCloseEmojiPicker = () => {
        setShowEmojiPicker(null);
    };

    const handleEmojiSelect = async (sparkId, emojiId, emojiChar) => {
        if (!user?.uid) return;
        setSparkReactions(prev => {
            const sparkReacts = { ...(prev[sparkId] || {}) };
            const currentReactors = sparkReacts[emojiId] || [];
            const hasReacted = currentReactors.includes(user.uid);
            if (hasReacted) {
                sparkReacts[emojiId] = currentReactors.filter(id => id !== user.uid);
            } else {
                sparkReacts[emojiId] = [...currentReactors, user.uid];
            }
            return { ...prev, [sparkId]: sparkReacts };
        });
        try {
            await reactToSpark(sparkId, emojiId, user.uid);
        } catch (error) {
            console.error('Error reacting to spark:', error);
        }
    };

    const handleReactionToggle = async (sparkId, emojiId) => {
        if (!user?.uid) return;
        setSparkReactions(prev => {
            const sparkReacts = { ...(prev[sparkId] || {}) };
            const currentReactors = sparkReacts[emojiId] || [];
            const hasReacted = currentReactors.includes(user.uid);
            if (hasReacted) {
                sparkReacts[emojiId] = currentReactors.filter(id => id !== user.uid);
            } else {
                sparkReacts[emojiId] = [...currentReactors, user.uid];
            }
            return { ...prev, [sparkId]: sparkReacts };
        });
        try {
            await reactToSpark(sparkId, emojiId, user.uid);
        } catch (error) {
            console.error('Error toggling reaction:', error);
        }
    };

    const getMediaType = (url) => {
        if (!url) return 'unknown';
        const lowerUrl = url.toLowerCase();
        if (lowerUrl.includes('spotify.com')) return 'spotify'; // Explicit Spotify check
        if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) return 'youtube'; // Explicit YouTube
        if (lowerUrl.includes('twitch.tv')) return 'twitch'; // Explicit Twitch
        if (lowerUrl.includes('vimeo.com') || lowerUrl.includes('dailymotion.com') || lowerUrl.includes('soundcloud.com') || lowerUrl.includes('mixcloud.com')) return 'video';
        if (lowerUrl.match(/\.(jpg|jpeg|png|gif|webp|svg)$/)) return 'image';
        if (lowerUrl.match(/\.(mp4|webm|ogg|mov|mp3|wav)$/) || ReactPlayer.canPlay(url)) return 'video'; // ReactPlayer can handle others
        if (lowerUrl.match(/\.(glb|gltf)$/)) return 'model';
        return 'unknown';
    };

    const renderTextWithEmotes = (text, source = {}, limit = 99) => {
        if (!text) return null;
        // Split by emote code pattern :Code: OR (image:N) OR (media:N)
        const parts = text.split(/(:[a-zA-Z0-9_]+:|\(image:\d+\)|\(media:\d+\))/g);
        let validImageCount = 0;

        return parts.map((part, index) => {
            // If part matches :Code:, try to resolve it
            if (/^:[a-zA-Z0-9_]+:$/.test(part)) {
                // Remove colons for strict lookup if needed, but hook handles :Code:
                const emoteObj = getEmoteObject(part);
                if (emoteObj) {
                    // If standard emoji (legacy)
                    if (emoteObj.emoji && !emoteObj.isCustom) {
                        return React.createElement('span', {
                            key: index,
                            title: part, // Tooltip for standard emojis
                            style: { fontSize: `${emoteSize}px`, verticalAlign: 'middle', cursor: 'help', lineHeight: '1' }
                        }, emoteObj.emoji);
                    }
                    // If custom emote (image)
                    if (emoteObj.displayUrl || emoteObj.url) {
                        return React.createElement('img', {
                            key: index,
                            src: emoteObj.displayUrl || emoteObj.url,
                            alt: part,
                            title: part, // Tooltip for custom emotes
                            style: {
                                height: `${emoteSize}px`, // DYNAMIC SIZE
                                width: 'auto',
                                verticalAlign: 'middle',
                                display: 'inline-block',
                                margin: '0 2px'
                            }
                        });
                    }
                }
            }
            // Otherwise just return text
            // Image/Media Token Match
            const imgMatch = part.match(/^(\(image:(\d+)\)|\(media:(\d+)\))$/);
            if (imgMatch) {
                // Determine index from either group 2 (image:N) or group 3 (media:N)
                const imgIndex = parseInt(imgMatch[2] || imgMatch[3], 10) - 1;
                let src = null;

                // Logic to find source
                if (source.isPreview && source.items && source.items[imgIndex]) {
                    src = source.items[imgIndex].preview;
                } else if (source.images && source.images[imgIndex]) {
                    src = source.images[imgIndex];
                } else if (source.imageUrl && imgIndex === 0) {
                    src = source.imageUrl; // Legacy Fallback
                }

                if (src) {
                    if (validImageCount < limit) {
                        validImageCount++;
                        const type = getMediaType(src);

                        // Container Style
                        const containerStyle = { margin: '10px 0', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.2)', background: 'rgba(0,0,0,0.2)' };

                        if (type === 'youtube') {
                            // Extract Video ID
                            let videoId = null;
                            const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
                            const match = src.match(regExp);
                            if (match && match[2].length === 11) videoId = match[2];

                            if (videoId) {
                                const embedUrl = `https://www.youtube.com/embed/${videoId}`;
                                console.log('Rendering YouTube Iframe:', embedUrl);
                                return React.createElement('div', { key: index, style: { ...containerStyle, height: '360px' } },
                                    React.createElement('iframe', {
                                        width: '100%',
                                        height: '360',
                                        src: embedUrl,
                                        title: "YouTube video player",
                                        frameBorder: "0",
                                        allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
                                        allowFullScreen: true
                                    })
                                );
                            } else {
                                // Fallback
                                return React.createElement('div', { key: index, style: containerStyle },
                                    React.createElement('a', { href: src, target: '_blank', style: { color: 'var(--primary)' } }, "Watch on YouTube")
                                );
                            }
                        }

                        if (type === 'video') {
                            console.log('Rendering Video Player:', src); // DEBUG
                            return React.createElement('div', { key: index, style: containerStyle },
                                React.createElement(ReactPlayer, { url: src, controls: true, width: '100%', height: '360px' })
                            );
                        }

                        if (type === 'twitch') {
                            // Manual Twitch Iframe
                            // URL formats: twitch.tv/username OR twitch.tv/videos/id
                            let embedUrl = null;
                            const parts = src.split('twitch.tv/');
                            if (parts.length > 1) {
                                const path = parts[1];
                                if (path.includes('videos/')) {
                                    // https://player.twitch.tv/?video=123456&parent=localhost
                                    const videoId = path.split('videos/')[1].split('?')[0];
                                    embedUrl = `https://player.twitch.tv/?video=${videoId}&parent=localhost&parent=127.0.0.1`;
                                } else {
                                    // https://player.twitch.tv/?channel=shroud&parent=localhost
                                    const channel = path.split('?')[0].split('/')[0];
                                    embedUrl = `https://player.twitch.tv/?channel=${channel}&parent=localhost&parent=127.0.0.1`;
                                }
                            }

                            if (embedUrl) {
                                return React.createElement('div', { key: index, style: { ...containerStyle, height: '360px' } },
                                    React.createElement('iframe', {
                                        src: embedUrl,
                                        height: "360",
                                        width: "100%",
                                        frameBorder: "0",
                                        scrolling: "no",
                                        allowFullScreen: true
                                    })
                                );
                            }
                            // Fallback to link if parsing fails
                            return React.createElement('div', { key: index, style: containerStyle },
                                React.createElement('a', { href: src, target: '_blank', style: { color: 'var(--primary)' } }, "Watch on Twitch")
                            );
                        }

                        if (type === 'spotify') {
                            // Convert standard URL to Embed URL if needed
                            // Standard: https://open.spotify.com/track/ID
                            // Embed: https://open.spotify.com/embed/track/ID
                            let embedSrc = src;
                            if (src.includes('open.spotify.com') && !src.includes('/embed/')) {
                                embedSrc = src.replace('open.spotify.com', 'open.spotify.com/embed');
                            }
                            return React.createElement('div', { key: index, style: { ...containerStyle, height: '80px' } }, // Compact height for spotify
                                React.createElement('iframe', {
                                    src: embedSrc,
                                    width: '100%',
                                    height: '80',
                                    frameBorder: '0',
                                    allow: 'encrypted-media',
                                    style: { borderRadius: '12px' }
                                })
                            );
                        }

                        if (type === 'model') {
                            return React.createElement('div', { key: index, style: { ...containerStyle, height: '300px' } },
                                React.createElement('model-viewer', {
                                    src: src,
                                    'auto-rotate': true,
                                    'camera-controls': true,
                                    style: { width: '100%', height: '100%' }
                                })
                            );
                        }

                        // Default to Image
                        return React.createElement('div', { key: index, style: containerStyle },
                            React.createElement('img', { src: src, style: { width: '100%', height: 'auto', display: 'block' } })
                        );
                    } else {
                        return React.createElement('span', { key: index, style: { color: 'var(--text-muted)', fontSize: '0.9em', fontStyle: 'italic' } }, ` (Media Limit Reached) `);
                    }
                }
            }
            return part;
        });
    };

    return React.createElement('div', { className: 'animate-fade-in', style: { maxWidth: '800px', margin: '0 auto' } },



        // Daily Challenge Banner
        dailyChallenge && React.createElement('div', { className: 'glass-panel', style: { position: 'relative', zIndex: 101, padding: '20px', marginBottom: '30px', background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.2), rgba(139, 92, 246, 0.2))', border: '1px solid var(--secondary)' } },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', color: 'var(--secondary)', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '1px' } },
                React.createElement(Trophy, { size: 16 }), "Daily Challenge",
                // Admin Regenerate Button
                user?.isSuperAdmin && React.createElement('button', {
                    onClick: async () => {
                        if (window.confirm("Force regenerate Daily Challenge? This updates it for everyone.")) {
                            await regenerateDailyChallenge();
                        }
                    },
                    title: "Force Regenerate (Super Admin)",
                    style: { marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#fbbf24', display: 'flex', alignItems: 'center' }
                }, React.createElement(Shield, { size: 16 }))
            ),
            React.createElement('div', { style: { display: 'flex', alignItems: 'flex-start', gap: '15px', marginBottom: '15px' } },
                React.createElement('img', {
                    src: "/images/mascots/MuseGod.png",
                    alt: "MuseGod",
                    style: { height: '70px', width: 'auto', objectFit: 'contain', filter: 'drop-shadow(0 0 10px rgba(139, 92, 246, 0.5))' },
                    onError: (e) => e.target.style.display = 'none'
                }),
                React.createElement('h2', { style: { fontSize: '24px', margin: 0, alignSelf: 'center' } }, dailyChallenge.prompt)
            ),

            // CHALLENGE STATUS BAR (BONUS / CLAIMED)
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', fontSize: '13px', background: 'rgba(0,0,0,0.2)', padding: '8px 12px', borderRadius: '6px' } },
                dailyChallenge.claimedBy ?
                    React.createElement('span', { style: { color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '5px' } },
                        React.createElement(Sparkles, { size: 14, color: '#666' }),
                        "Sparkle claimed by ",
                        React.createElement('button', {
                            onClick: () => onViewProfile && onViewProfile(dailyChallenge.claimedBy),
                            style: {
                                fontWeight: 'bold',
                                color: 'var(--primary)',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '0 2px',
                                textDecoration: 'underline',
                                fontSize: 'inherit'
                            }
                        }, dailyChallenge.claimedByName || "a fast creator")
                    ) :
                    React.createElement('span', { className: 'animate-pulse', style: { color: '#fda4af', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' } },
                        React.createElement(Sparkles, { size: 14, color: '#f472b6' }),
                        "First to Spark wins a pinned badge!"
                    )
            ),

            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
                React.createElement('button', {
                    className: 'btn',
                    onClick: () => { setSelectedInspiration(dailyChallenge.prompt); setSelectedInspirationType('challenge'); },
                    style: { fontSize: '14px', padding: '8px 16px' }
                }, "Accept Challenge"),

                // History Dropdown (Super Admin Only)
                user?.isSuperAdmin && React.createElement('div', { style: { position: 'relative' } },
                    React.createElement('button', {
                        className: 'btn-ghost',
                        onClick: handleLoadHistory,
                        title: "Challenge History",
                        style: { color: 'var(--text-muted)' }
                    }, React.createElement(Clock, { size: 20 })),

                    showAdminHistoryDropdown && React.createElement('div', { className: 'glass-panel', style: { position: 'absolute', top: '100%', right: 0, width: '300px', maxHeight: '300px', overflowY: 'auto', zIndex: 1000, padding: '10px', background: '#1F1F23', border: '1px solid var(--border)' } },
                        React.createElement('h4', { style: { fontSize: '12px', color: 'var(--text-muted)', marginBottom: '10px', paddingBottom: '5px', borderBottom: '1px solid var(--border)' } }, "Past Daily Challenges"),
                        challengeHistory.length === 0 ? React.createElement('div', { style: { padding: '10px', fontSize: '12px', color: '#666' } }, "No history yet.") :
                            challengeHistory.map(item =>
                                React.createElement('div', {
                                    key: item.id,
                                    style: { padding: '10px', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: '13px', display: 'flex', gap: '10px', alignItems: 'flex-start' }
                                },
                                    React.createElement('div', { style: { flex: 1 } },
                                        React.createElement('div', { style: { color: 'var(--text-muted)', fontSize: '11px', marginBottom: '2px' } },
                                            item.date || "Unknown Date"
                                        ),
                                        React.createElement('div', { style: { color: 'var(--text-primary)', marginBottom: '4px' } }, item.prompt),
                                        // Stats Row
                                        React.createElement('div', { style: { display: 'flex', gap: '8px', fontSize: '10px', color: 'var(--text-muted)' } },
                                            // Usage Count
                                            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '3px' } },
                                                React.createElement(Sparkles, { size: 10, color: '#d946ef' }), // Purple sparkles
                                                item.usageCount > 999 ? '999+' : (item.usageCount || 0)
                                            ),
                                            // Likes Count
                                            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '3px' } },
                                                React.createElement(Heart, { size: 10, color: '#ec4899', fill: '#ec4899' }), // Pink heart filled
                                                item.totalLikes > 999 ? '999+' : (item.totalLikes || 0)
                                            )
                                        )
                                    ),
                                    React.createElement('button', {
                                        onClick: () => handleRestoreChallenge(item),
                                        title: "Restore this challenge",
                                        style: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', padding: '5px' }
                                    }, React.createElement(RotateCcw, { size: 14 }))
                                )
                            )
                    )
                )
            )
        ),

        // Input Area
        React.createElement('div', {
            className: 'glass-panel',
            style: {
                position: 'relative',
                zIndex: 100,
                padding: '25px',
                marginBottom: '40px',
                border: '1px solid var(--primary)',
                boxShadow: '0 0 30px rgba(139, 92, 246, 0.15)',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)'
            }
        },
            // Live Preview
            newSpark && React.createElement('div', {
                className: 'animate-fade-in',
                style: {
                    padding: '12px',
                    marginBottom: '10px',
                    background: 'rgba(0,0,0,0.2)',
                    borderRadius: '8px',
                    minHeight: '24px',
                    fontSize: `${fontSize}px`,
                    lineHeight: '1.5',
                    border: '1px solid rgba(255,255,255,0.05)'
                }
            },
                // Live Preview Container
                React.createElement('div', { style: { fontSize: '10px', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' } },
                    React.createElement(Sparkles, { size: 10 }), "Live Preview"
                ),
                // Full Content Area (No Scroll)
                React.createElement('div', { style: { marginBottom: '10px' } },
                    renderTextWithEmotes(newSpark, { isPreview: true, items: attachments }, getImageLimit())
                ),

                // Footer Info (Moved to Left)
                React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-start', fontSize: '10px', color: 'var(--text-muted)', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '4px' } },
                    `Used: ${(newSpark.match(/(\(image:\d+\)|\(media:\d+\))/g) || []).length}/${getImageLimit()}`
                ),
                // Live Image Gallery Preview (Drafts)
                attachments.length > 0 && React.createElement('div', { style: { marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' } },
                    attachments.map((att, idx) => {
                        const type = getMediaType(att.preview);
                        return React.createElement('div', {
                            key: att.id,
                            onClick: () => {
                                const currentCount = (newSpark.match(/(\(image:\d+\)|\(media:\d+\))/g) || []).length;
                                if (currentCount >= getImageLimit()) {
                                    alert(`Limit reached! You can only use ${getImageLimit()} slots.`);
                                    return;
                                }
                                setNewSpark(prev => prev + ` (media:${idx + 1}) `);
                                // Keep focus on controls
                                scrollToToolbar();
                            },
                            title: "Click to insert media",
                            style: { position: 'relative', width: '60px', height: '60px', borderRadius: '4px', overflow: 'hidden', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }
                        },
                            type === 'image' ?
                                React.createElement('img', { src: att.preview, style: { width: '100%', height: '100%', objectFit: 'cover' } }) :
                                type === 'youtube' ? React.createElement(Youtube, { size: 24, color: '#FF0000' }) :
                                    type === 'spotify' ? React.createElement(Headphones, { size: 24, color: '#1DB954' }) :
                                        type === 'twitch' ? React.createElement(Twitch, { size: 24, color: '#9146FF' }) :
                                            type === 'video' ? React.createElement(Video, { size: 24, color: 'var(--text-main)' }) :
                                                type === 'model' ? React.createElement(Box, { size: 24, color: '#3b82f6' }) :
                                                    React.createElement(Link, { size: 24, color: 'var(--text-muted)' }),
                            React.createElement('div', { style: { position: 'absolute', bottom: 0, right: 0, background: 'rgba(0,0,0,0.7)', color: 'white', fontSize: '10px', padding: '2px 4px' } }, `:${idx + 1}`)
                        );
                    })
                )
            ),

            React.createElement('textarea', {
                value: newSpark,
                onChange: (e) => setNewSpark(e.target.value),
                placeholder: "Share your art or a spark of inspiration...",
                style: {
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-main)',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    resize: 'none',
                    outline: 'none',
                    minHeight: '60px',
                    marginBottom: '10px'
                }
            }),

            // Attachments Preview
            (selectedInspiration) && React.createElement('div', { style: { display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' } },
                selectedInspiration && React.createElement('div', {
                    style: {
                        background: selectedInspirationType === 'challenge' ? 'rgba(236, 72, 153, 0.2)' : 'rgba(139, 92, 246, 0.2)',
                        color: selectedInspirationType === 'challenge' ? '#ec4899' : 'var(--primary)',
                        padding: '5px 10px',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        fontSize: '12px',
                        border: selectedInspirationType === 'challenge' ? '1px solid #ec4899' : '1px solid var(--primary)'
                    }
                },
                    selectedInspirationType === 'challenge' ? React.createElement(Flame, { size: 14 }) : React.createElement(Sparkles, { size: 14 }),
                    (selectedInspirationType === 'challenge' ? "Challenge: " : "Muse: ") + selectedInspiration.substring(0, 20) + "...",
                    React.createElement('button', {
                        onClick: () => { setSelectedInspiration(null); setSelectedInspirationType('muse'); },
                        style: { background: 'none', border: 'none', color: selectedInspirationType === 'challenge' ? '#ec4899' : 'var(--primary)', cursor: 'pointer', marginLeft: '5px', display: 'flex', alignItems: 'center' }
                    }, React.createElement(X, { size: 14 }))
                )
            ),

            // Toolbar
            React.createElement('div', { id: 'spark-controls-toolbar', style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border)', paddingTop: '15px' } },
                React.createElement('div', { style: { display: 'flex', gap: '10px' } },
                    // Image Input Toggle
                    React.createElement('div', { style: { position: 'relative' } },
                        React.createElement('button', {
                            className: 'btn-ghost',
                            onClick: () => { setShowImageInput(!showImageInput); scrollToToolbar(); },
                            title: `Add Media (${attachments.length}/${getImageLimit()})`,
                            style: { color: attachments.length > 0 || showImageInput ? 'var(--secondary)' : 'inherit' }
                        }, React.createElement(ImageIcon, { size: 20 })),



                        // Image Input Dropdown (Now a Portal Modal)
                        showImageInput && createPortal(
                            React.createElement('div', {
                                style: {
                                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                                    zIndex: 99999,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: 'var(--backdrop)',
                                    backdropFilter: 'blur(4px)',
                                    animation: 'fadeIn 0.2s ease-out'
                                },
                                onClick: (e) => { if (e.target === e.currentTarget) setShowImageInput(false); }
                            },
                                React.createElement('div', {
                                    className: 'glass-panel',
                                    style: {
                                        width: '400px',
                                        maxWidth: '90vw',
                                        padding: '20px',
                                        background: 'var(--bg-dark)',
                                        border: '1px solid var(--primary)',
                                        boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
                                        borderRadius: '16px',
                                        display: 'flex', flexDirection: 'column', gap: '15px'
                                    }
                                },
                                    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)', paddingBottom: '10px', marginBottom: '5px' } },
                                        React.createElement('span', { style: { fontSize: '14px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' } },
                                            React.createElement(ImageIcon, { size: 16, color: 'var(--primary)' }),
                                            `Add Media (${attachments.length}/${getImageLimit()})`
                                        ),
                                        React.createElement('button', { onClick: () => setShowImageInput(false), style: { background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' } }, React.createElement(X, { size: 18 }))
                                    ),

                                    // List Attachments
                                    attachments.length > 0 && React.createElement('div', { style: { display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '10px', marginBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.1)' } },
                                        attachments.map((att, idx) => {
                                            const type = getMediaType(att.preview);
                                            return React.createElement('div', { key: att.id, style: { position: 'relative', minWidth: '60px', width: '60px', height: '60px', borderRadius: '6px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                                                type === 'image' ?
                                                    React.createElement('img', { src: att.preview, style: { width: '100%', height: '100%', objectFit: 'cover' } }) :
                                                    type === 'youtube' ? React.createElement(Youtube, { size: 24, color: '#FF0000' }) :
                                                        type === 'spotify' ? React.createElement(Headphones, { size: 24, color: '#1DB954' }) :
                                                            type === 'twitch' ? React.createElement(Twitch, { size: 24, color: '#9146FF' }) :
                                                                type === 'video' ? React.createElement(Video, { size: 24, color: 'var(--text-main)' }) :
                                                                    type === 'model' ? React.createElement(Box, { size: 24, color: '#3b82f6' }) :
                                                                        React.createElement(Link, { size: 24, color: 'var(--text-muted)' }),
                                                React.createElement('button', {
                                                    onClick: () => removeAttachment(att.id, idx),
                                                    style: { position: 'absolute', top: 0, right: 0, background: 'rgba(0,0,0,0.6)', border: 'none', color: 'white', cursor: 'pointer', padding: '2px' }
                                                }, React.createElement(X, { size: 12 })),
                                                React.createElement('div', { style: { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.6)', color: 'white', fontSize: '9px', textAlign: 'center' } }, `:${idx + 1}`)
                                            )
                                        })
                                    ),

                                    React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
                                        // Upload Button
                                        React.createElement('label', {
                                            className: 'btn-secondary',
                                            style: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', opacity: attachments.length >= getImageLimit() ? 0.5 : 1 }
                                        },
                                            React.createElement('input', {
                                                type: 'file',
                                                accept: 'image/*',
                                                onChange: handleImageSelect,
                                                disabled: attachments.length >= getImageLimit(),
                                                style: { display: 'none' }
                                            }),
                                            React.createElement(ImageIcon, { size: 16 }), "Upload Image"
                                        ),
                                        // URL Input
                                        React.createElement('div', { style: { display: 'flex', gap: '8px' } },
                                            React.createElement('input', {
                                                type: 'text',
                                                placeholder: "Paste Image, Video, or 3D URL...",
                                                value: urlInput,
                                                onChange: (e) => setUrlInput(e.target.value),
                                                style: { flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid var(--border)', background: 'rgba(0,0,0,0.2)', color: 'white', fontSize: '12px' }
                                            }),
                                            React.createElement('button', {
                                                className: 'btn-secondary',
                                                onClick: handleAddUrlImage,
                                                disabled: !urlInput.trim() || attachments.length >= getImageLimit(),
                                                style: { padding: '8px 12px' }
                                            }, "Add")
                                        ),
                                        // Supported Media Help
                                        React.createElement('div', { style: { marginTop: '15px', paddingTop: '15px', borderTop: '1px solid rgba(255,255,255,0.1)' } },
                                            React.createElement('div', { style: { fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '5px' } },
                                                React.createElement(Info, { size: 12 }), "Supported Formats"
                                            ),
                                            React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '8px' } },
                                                [
                                                    { icon: Youtube, label: "YouTube", color: "#FF0000" },
                                                    { icon: Twitch, label: "Twitch", color: "#9146FF" },
                                                    { icon: Headphones, label: "Spotify", color: "#1DB954" },
                                                    { icon: Box, label: "3D Models .glb", color: "#3b82f6" },
                                                    { icon: Video, label: "MP4 / MOV", color: "var(--text-main)" },
                                                    { icon: ImageIcon, label: "Images", color: "var(--secondary)" }
                                                ].map((item, idx) =>
                                                    React.createElement('div', {
                                                        key: idx, style: {
                                                            display: 'flex', alignItems: 'center', gap: '4px',
                                                            background: 'rgba(255,255,255,0.05)',
                                                            padding: '4px 8px', borderRadius: '4px',
                                                            fontSize: '11px', color: 'var(--text-muted)'
                                                        }
                                                    },
                                                        React.createElement(item.icon, { size: 12, color: item.color }),
                                                        item.label
                                                    )
                                                )
                                            )
                                        )
                                    )
                                )
                            ), document.body)),

                    // Camera Button (Direct Capture)
                    React.createElement('button', {
                        className: 'btn-ghost',
                        onClick: () => setShowCameraModal(true),
                        title: "Snap a Photo",
                        style: { cursor: 'pointer', color: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'none' }
                    },
                        React.createElement(Camera, { size: 20 })
                    ),

                    // Camera Modal Portal
                    showCameraModal && React.createElement(CameraModal, {
                        onCapture: handleCameraCapture,
                        onClose: () => setShowCameraModal(false)
                    }),

                    // Post Emote Picker
                    React.createElement('div', { style: { position: 'relative' } },
                        React.createElement('button', {
                            className: 'btn-ghost',
                            onClick: () => { setShowPostEmotePicker(!showPostEmotePicker); scrollToToolbar(); },
                            title: "Add Emote",
                            style: { color: showPostEmotePicker ? 'var(--secondary)' : 'inherit' }
                        }, React.createElement(Smile, { size: 20 })),

                        showPostEmotePicker && React.createElement('div', {
                            style: { position: 'absolute', bottom: '100%', left: 0, zIndex: 1000, marginBottom: '10px' }
                        },
                            React.createElement(EmotePicker, {
                                user: user,
                                userTier: user?.subscriptionTier || 'global',
                                mode: 'chat', // 'chat' returns the code (e.g. :MuseHappy:)
                                onSelect: (code) => {
                                    setNewSpark(prev => prev + code + ' ');
                                    setShowPostEmotePicker(false);
                                },
                                onClose: () => setShowPostEmotePicker(false)
                            })
                        )
                    ),

                ),
                React.createElement('div', { style: { display: 'flex', gap: '10px', alignItems: 'center' } },
                    React.createElement('div', { style: { position: 'relative' } },
                        React.createElement('button', {
                            className: 'btn-ghost',
                            onClick: () => { setShowInspirationDropdown(!showInspirationDropdown); scrollToToolbar(); },
                            title: "Link Inspiration",
                            style: { color: selectedInspiration ? 'var(--primary)' : 'inherit' }
                        }, React.createElement(Sparkles, { size: 20 })),

                        showInspirationDropdown && React.createElement('div', { className: 'glass-panel', style: { position: 'absolute', top: '100%', right: 0, width: '300px', maxHeight: '200px', overflowY: 'auto', zIndex: 99999, padding: '10px', background: 'var(--bg-card)', border: '1px solid var(--border)', boxShadow: '0 5px 20px rgba(0,0,0,0.5)' } },
                            React.createElement('h4', { style: { fontSize: '12px', color: 'var(--text-muted)', marginBottom: '5px' } }, "Select from History"),
                            history.length === 0 ? React.createElement('div', { style: { padding: '10px', fontSize: '12px', color: 'var(--text-muted)' } }, "No history yet.") :
                                history.map(item =>
                                    React.createElement('div', {
                                        key: item.id,
                                        onClick: () => { setSelectedInspiration(item.prompt); setShowInspirationDropdown(false); setSelectedInspirationType('muse'); },
                                        style: { padding: '8px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid var(--border)', color: 'var(--text-primary)' }
                                    }, item.prompt)
                                )
                        )
                    ),
                    React.createElement('button', {
                        className: 'btn',
                        onClick: () => { handlePost(); scrollToToolbar(); }
                        // Disabled removed to allow modal Check
                    }, 'Ignite Spark')
                )
            ),

        ),

        // (Inline Limit Modal Removed - Using Portal at end of file)

        // Feed Grid
        React.createElement('div', { style: { display: 'grid', gap: '20px' } },
            sparks
                .filter(spark => {
                    const isAuthorBanned = bannedUserIds.includes(spark.authorId);
                    // Filter OUT if author is banned AND current user is NOT an admin
                    const isAdmin = user?.isAdmin || user?.isSuperAdmin;
                    if (isAuthorBanned && !isAdmin) {
                        return false;
                    }
                    return true;
                })
                .map((spark, index) => {
                    const isAuthorBanned = bannedUserIds.includes(spark.authorId);
                    const isAdmin = user?.isAdmin || user?.isSuperAdmin;
                    const isContentDimmed = isAuthorBanned && isAdmin;
                    const isAuthor = user?.uid && spark.authorId === user.uid;

                    return React.createElement('div', {
                        key: spark.id,
                        id: `spark-${spark.id}`, // FOR DEEP LINK SCROLLING
                        className: 'glass-panel',
                        style: {
                            padding: '20px',
                            opacity: isContentDimmed ? 0.6 : 1,
                            border: isContentDimmed ? '1px solid #ef4444' : undefined,
                            position: 'relative' // For emoji picker positioning
                        }
                    },
                        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', marginBottom: '10px' } },
                            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '10px' } },
                                React.createElement('img', {
                                    src: (user && spark.authorId === user.uid) ? user.avatar : (spark.authorAvatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${spark.author}`),
                                    style: {
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '50%',
                                        border: isFriend(spark.authorId) ? '2px solid var(--primary)' : '2px solid var(--border)',
                                        boxShadow: isFriend(spark.authorId) ? '0 0 8px rgba(139, 92, 246, 0.5)' : 'none',
                                        filter: isContentDimmed ? 'grayscale(100%)' : 'none'
                                    }
                                }),
                                React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '6px' } },
                                    React.createElement('button', {
                                        onClick: () => onViewProfile && onViewProfile(spark.authorId),
                                        style: {
                                            fontWeight: 'bold',
                                            color: 'var(--primary)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: '15px',
                                            textDecoration: 'underline',
                                            textDecorationColor: 'transparent',
                                            transition: 'text-decoration-color 0.2s',
                                            display: 'flex',
                                            alignItems: 'center'
                                        },
                                        onMouseEnter: (e) => e.target.style.textDecorationColor = 'var(--primary)',
                                        onMouseLeave: (e) => e.target.style.textDecorationColor = 'transparent'
                                    },
                                        spark.author,
                                        React.createElement(LiveUserBadge, { userId: spark.authorId, initialTier: spark.authorTier, size: 24 })
                                    ),
                                    // Banned Indicator for Admins
                                    isContentDimmed && React.createElement('span', {
                                        style: { fontSize: '10px', color: '#ef4444', fontWeight: 'bold', border: '1px solid #ef4444', padding: '2px 4px', borderRadius: '4px' }
                                    }, "BANNED"),

                                    // Timestamp
                                    React.createElement('span', {
                                        style: { fontSize: '12px', color: 'var(--text-muted)', marginLeft: '4px' }
                                    }, " • " + formatTimeAgo(spark.createdAt)),

                                    // FIRST TO SPARK BADGE
                                    // Robust check: Trust Spark doc OR System Doc
                                    (spark.isFirstSpark || (dailyChallenge && dailyChallenge.claimedSparkId === spark.id)) && React.createElement('div', {
                                        title: '🏆 First to Spark Reward',
                                        style: {
                                            marginLeft: '5px',
                                            padding: '2px 6px',
                                            borderRadius: '12px',
                                            background: 'linear-gradient(90deg, #ec4899, #8b5cf6)',
                                            color: 'white',
                                            fontSize: '10px',
                                            fontWeight: 'bold',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '3px',
                                            boxShadow: '0 0 10px rgba(236, 72, 153, 0.4)'
                                        }
                                    }, React.createElement(Sparkles, { size: 10, fill: 'white' }), "First Spark 🏆"),

                                    // Magic Spark indicator
                                    magicSparkHolder === spark.author && React.createElement('div', {
                                        title: '✨ Magic Spark Holder',
                                        style: {
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #ff0080, #ff8c00, #40e0d0, #ff0080)',
                                            backgroundSize: '300% 300%',
                                            animation: 'gradient 3s ease infinite',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 0 10px rgba(255, 0, 128, 0.4)'
                                        }
                                    },
                                        React.createElement(Sparkles, { size: 12, color: 'white', fill: 'white' })
                                    )
                                )
                            ),

                        ),

                        spark.content && React.createElement('div', {
                            style: { fontSize: `${fontSize}px`, lineHeight: '1.5', marginBottom: '15px' }
                        }, renderTextWithEmotes(spark.content, spark,
                            spark.authorTier === 'super_admin' ? 99 :
                                (spark.authorTier === 'admin' ? 6 :
                                    (TIER_IMAGE_LIMITS[spark.authorTier] || 1))
                        )),

                        // Legacy Image Support: Only show if NO V2 images presence
                        (spark.imageUrl && (!spark.images || spark.images.length === 0)) && React.createElement('div', { style: { marginBottom: '15px', borderRadius: '12px', overflow: 'hidden' } },
                            React.createElement('img', {
                                src: spark.imageUrl,
                                style: {
                                    width: '100%',
                                    height: 'auto',
                                    display: 'block',
                                    filter: isContentDimmed ? 'grayscale(100%)' : 'none'
                                }
                            })
                        ),

                        spark.inspiration && React.createElement('div', {
                            style: {
                                background: (spark.inspirationType === 'challenge' || (dailyChallenge && spark.inspiration === dailyChallenge.prompt)) ? 'rgba(236, 72, 153, 0.1)' : 'rgba(139, 92, 246, 0.1)',
                                padding: '10px',
                                borderRadius: '8px',
                                marginBottom: '15px',
                                borderLeft: (spark.inspirationType === 'challenge' || (dailyChallenge && spark.inspiration === dailyChallenge.prompt)) ? '3px solid #ec4899' : '3px solid var(--primary)'
                            }
                        },
                            React.createElement('div', {
                                style: {
                                    fontSize: '10px',
                                    color: (spark.inspirationType === 'challenge' || (dailyChallenge && spark.inspiration === dailyChallenge.prompt)) ? '#ec4899' : 'var(--primary)',
                                    textTransform: 'uppercase',
                                    fontWeight: 'bold',
                                    display: 'flex', alignItems: 'center', gap: '5px'
                                }
                            },
                                (spark.inspirationType === 'challenge' || (dailyChallenge && spark.inspiration === dailyChallenge.prompt)) ?
                                    [React.createElement(Flame, { size: 12, key: 'icon' }), "Daily Challenge"] :
                                    [React.createElement(Sparkles, { size: 12, key: 'icon' }), "Inspired by Muse"]
                            ),
                            React.createElement('div', { style: { fontSize: '14px', fontStyle: 'italic' } }, spark.inspiration)
                        ),

                        React.createElement(EmojiReactionBar, {
                            reactions: sparkReactions[spark.id] || spark.reactions || {},
                            currentUserId: user?.uid,
                            onReactionToggle: (emojiId) => handleReactionToggle(spark.id, emojiId),
                            getEmoteObject: getEmoteObject, // Pass hook helper
                            checkAccess: (id) => {
                                if (isAuthor) return { hasAccess: false, reason: "You cannot react to your own spark." };
                                return checkEmoteAccess(id, user);
                            }
                        }),

                        React.createElement('div', { style: { display: 'flex', gap: '20px', marginTop: '15px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '15px' } },
                            // LIKE
                            React.createElement('button', {
                                onClick: () => !isAuthor && handleLike(spark.id),
                                title: isAuthor ? "You cannot like your own spark" : "Like",
                                style: {
                                    background: 'none',
                                    border: 'none',
                                    cursor: isAuthor ? 'default' : 'pointer',
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    color: likedSparks[spark.id] ? '#ec4899' : '#999',
                                    transition: 'all 0.2s',
                                    opacity: isAuthor ? 0.5 : 1
                                }
                            },
                                React.createElement(Heart, { size: 18, fill: likedSparks[spark.id] ? '#ec4899' : 'none' }),
                                React.createElement('span', { style: { fontSize: '14px' } }, spark.likes || 0)
                            ),
                            React.createElement('button', {
                                onClick: () => !isAuthor && handleOpenEmojiPicker(spark.id),
                                title: isAuthor ? "You cannot react to your own spark" : 'React with emoji',
                                style: {
                                    background: 'none',
                                    border: 'none',
                                    cursor: isAuthor ? 'default' : 'pointer',
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    color: '#999',
                                    transition: 'all 0.2s',
                                    opacity: isAuthor ? 0.5 : 1
                                }
                            },
                                React.createElement(Smile, { size: 18 })
                            ),
                            // SHARE
                            React.createElement('button', {
                                onClick: () => handleShareClick(spark),
                                style: { background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#999' }
                            },
                                React.createElement(Share2, { size: 18 }),
                                React.createElement('span', { style: { fontSize: '14px' } }, "Share")
                            ),
                            // DELETE or REPORT
                            ((user?.id === spark.authorId) || (user?.uid === spark.authorId)) ?
                                React.createElement('button', {
                                    onClick: () => handleDelete(spark.id),
                                    style: { background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#ef4444', marginLeft: 'auto' }
                                }, React.createElement(Trash2, { size: 16 }), " Delete")
                                :
                                React.createElement('button', {
                                    onClick: () => handleReport(spark.id),
                                    style: { background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#999', marginLeft: 'auto' }
                                },
                                    React.createElement(Flag, { size: 16 }),
                                    // Subtle report
                                )
                        ),

                        // Emote Picker (Replaces EmojiPicker)
                        showEmojiPicker === spark.id && React.createElement(EmotePicker, {
                            user: user,
                            userTier: user?.subscriptionTier || 'global',
                            mode: 'reaction',
                            onSelect: (val) => handleEmojiSelect(spark.id, val),
                            onClose: handleCloseEmojiPicker
                        })
                    );
                })
        ),

        // Link Confirmation Modal
        // Link Confirmation Modal
        // Link Confirmation Modal
        showLinkConfirmModal && createPortal(
            React.createElement('div', {
                style: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }
            },
                React.createElement('div', { className: 'glass-panel', style: { padding: '24px', maxWidth: '400px', width: '90%', textAlign: 'center', background: '#18181b', borderRadius: '16px', border: '1px solid var(--border)' } },
                    React.createElement(Sparkles, { size: 32, color: 'var(--primary)', style: { marginBottom: '16px' } }),
                    React.createElement('h3', { style: { fontSize: '20px', marginBottom: '8px', color: 'white' } }, "Missing Inspiration?"),
                    React.createElement('p', { style: { color: '#999', marginBottom: '24px', lineHeight: '1.5' } },
                        "Every Spark needs a Muse! It looks like you haven't linked one yet."
                    ),
                    React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '12px' } },
                        // Option 1: Daily Challenge
                        dailyChallenge && React.createElement('button', {
                            className: 'btn-primary',
                            onClick: () => {
                                setSelectedInspiration(dailyChallenge.prompt);
                                setSelectedInspirationType('challenge');
                                setShowLinkConfirmModal(false);
                            },
                            style: { width: '100%', justifyContent: 'center', borderRadius: '12px', padding: '12px' }
                        },
                            React.createElement(Flame, { size: 18, style: { marginRight: '8px' } }),
                            "Link Daily Challenge"
                        ),
                        // Option 2: Select from History
                        React.createElement('button', {
                            className: 'btn-secondary',
                            onClick: () => {
                                setShowInspirationDropdown(true);
                                setSelectedInspirationType('muse'); // Will be confirmed in dropdown
                                setShowLinkConfirmModal(false);
                            },
                            style: { width: '100%', justifyContent: 'center', borderRadius: '12px', padding: '12px' }
                        },
                            React.createElement(Clock, { size: 18, style: { marginRight: '8px' } }),
                            "Select from Muse History"
                        ),
                        // Option 3: Cancel
                        React.createElement('button', {
                            className: 'btn-ghost',
                            onClick: () => setShowLinkConfirmModal(false),
                            style: { width: '100%', justifyContent: 'center', borderRadius: '12px', color: '#666' }
                        }, "Cancel")
                    )
                )
            ),
            document.body
        ),

        // Limit Modal
        showLimitModal && createPortal(
            React.createElement('div', {
                style: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }
            },
                React.createElement('div', { className: 'glass-panel', style: { padding: '24px', maxWidth: '400px', width: '90%', textAlign: 'center', background: '#18181b', borderRadius: '16px', border: '1px solid var(--border)' } },
                    React.createElement(Trophy, { size: 32, color: '#f59e0b', style: { marginBottom: '16px' } }),
                    React.createElement('h3', { style: { fontSize: '20px', marginBottom: '8px', color: 'white' } }, "Daily Limit Reached"),
                    React.createElement('p', { style: { color: '#999', marginBottom: '24px', lineHeight: '1.5' } },
                        `You've reached your limit of ${limitDetails.limit} Sparks for today. Upgrade your tier to ignite more!`
                    ),
                    React.createElement('button', {
                        className: 'btn-primary',
                        onClick: () => setShowLimitModal(false),
                        style: { width: '100%', justifyContent: 'center', borderRadius: '12px' }
                    }, "Understood")
                )
            ),
            document.body
        ),

        // SHARE MODAL
        shareModalOpen && createPortal(
            React.createElement('div', {
                className: 'modal-overlay', // Uses theme var(--backdrop)
                onClick: () => setShareModalOpen(false)
            },
                React.createElement('div', {
                    className: 'modal-content', // Uses theme var(--bg-dark), var(--text-main)
                    onClick: (e) => e.stopPropagation(),
                    style: { display: 'flex', flexDirection: 'column', gap: '20px' } // Layout only
                },

                    // Close Button
                    React.createElement('button', {
                        onClick: () => setShareModalOpen(false),
                        style: { position: 'absolute', top: '15px', right: '15px', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }
                    }, React.createElement(X, { size: 24 })),

                    React.createElement('h3', { style: { fontSize: '24px', margin: 0, textAlign: 'center' } }, "Share Spark"),

                    // Option A: Link
                    React.createElement('div', { style: { padding: '15px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' } },
                        React.createElement('div', { style: { fontSize: '14px', color: '#999', marginBottom: '8px' } }, "Share Deep Link"),
                        React.createElement('div', { style: { display: 'flex', gap: '10px' } },
                            React.createElement('input', {
                                readOnly: true,
                                value: `${window.location.origin}/?spark=${activeShareSpark?.id}${user?.referralCode ? `&ref=${user.referralCode}` : ''}`,
                                style: { flex: 1, background: 'rgba(0,0,0,0.3)', border: '1px solid #333', padding: '10px', borderRadius: '8px', color: 'white' }
                            }),
                            React.createElement('button', {
                                className: 'btn', // Standard site button
                                onClick: () => {
                                    const link = `${window.location.origin}/?spark=${activeShareSpark?.id}${user?.referralCode ? `&ref=${user.referralCode}` : ''}`;
                                    navigator.clipboard.writeText(link);
                                    alert("Link copied to clipboard!");
                                },
                                style: { display: 'flex', alignItems: 'center', gap: '6px' }
                            },
                                React.createElement(Copy, { size: 16 }),
                                "Copy"
                            )
                        )
                    ),

                    React.createElement('div', { style: { textAlign: 'center', color: '#666', fontSize: '12px' } }, "— OR —"),

                    // Option B: Visual Card
                    React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '15px' } },
                        /* Show Generated Image OR Placeholder */
                        generatedCardImage ?
                            React.createElement('img', {
                                src: generatedCardImage,
                                style: { width: '100%', borderRadius: '12px', boxShadow: '0 5px 20px rgba(0,0,0,0.5)' }
                            }) :
                            React.createElement('div', {
                                style: {
                                    height: '200px',
                                    background: 'var(--bg-card)', // Theme aware
                                    borderRadius: '12px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '10px',
                                    border: '2px dashed var(--border)' // Theme aware
                                }
                            },
                                isGeneratingCard ? React.createElement('span', { className: 'animate-spin' }, "⏳") : React.createElement(ImageIcon, { size: 32, color: 'var(--text-muted)' }),
                                React.createElement('span', { style: { color: 'var(--text-muted)', fontSize: '14px' } }, isGeneratingCard ? "Sublimating pixels..." : "Preview will appear here")
                            ),

                        !generatedCardImage && React.createElement('button', {
                            className: 'btn', // Standard site button
                            onClick: generateShareCard,
                            disabled: isGeneratingCard,
                            style: { justifyContent: 'center', width: '100%' }
                        }, isGeneratingCard ? "Generating..." : "Generate Visual Card"),

                        generatedCardImage && React.createElement('a', {
                            href: generatedCardImage,
                            download: `spark-${activeShareSpark?.id}.png`,
                            className: 'btn', // Standard site button
                            style: { justifyContent: 'center', textDecoration: 'none', width: '100%', display: 'flex', alignItems: 'center', gap: '8px' }
                        },
                            React.createElement(Download, { size: 18 }),
                            "Download Image"
                        )
                    )
                )
            ),
            document.body
        ),

        // HIDDEN RENDER CONTAINER (For html2canvas)
        activeShareSpark && React.createElement('div', {
            style: { position: 'absolute', top: '-9999px', left: '-9999px' }
        },
            React.createElement(SparkShareCard, {
                ref: cardRef,
                spark: activeShareSpark,
                author: activeShareSpark.author,
                getEmoteUrl: getEmoteUrl
            })
        )
    ); // End Main Feed createElement
} // End Feed Function
